import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { NewUploadSessionResponse } from '../../Model/responseDtos/NewUploadSessionResponse';
import { StartUploadSessionRequest } from '../../Model/requests/StartUploadSessionRequest';
import { useContext } from 'react';
import ServicesContext from '../../global/services/ServicesContext';
import { useNotificationContext } from '../../global/hooks/useNotificationContext';
import { mutateDocumentsKeys } from './mutateKeyFactory';
import { GEAR_NOTIFICATION_TYPE } from '../../Model/enums/GearNotificationType';
import { v4 as uuidv4 } from 'uuid';
import { useGlobalizationContext } from '../globalization/GlobalizationContext';
import { useTranslation } from '../globalization/useTranslation';
import { FileHelper } from '../../helpers/FileHelper';

export function useStartUploadSession(): UseMutationResult<
  NewUploadSessionResponse,
  unknown,
  { dto: StartUploadSessionRequest }
> {
  const servicesCtx = useContext(ServicesContext);
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { addCenterNotification, updateCenterNotification, showToast } = useNotificationContext();

  const startUploadSession = (data: { dto: StartUploadSessionRequest }): Promise<NewUploadSessionResponse> => {
    if (servicesCtx?.documentsService && data.dto) {
      return servicesCtx.documentsService?.startUploadSession(data.dto);
    }
    throw new Error('Services context not initialized');
  };

  return useMutation({
    mutationKey: mutateDocumentsKeys.startUploadSession,
    mutationFn: (data: { dto: StartUploadSessionRequest }) => startUploadSession(data),
    onSuccess: (result, variables) => {
      const fileName = variables.dto.chunk.file.name;
      const fileSize = variables.dto.chunk.file.size;
      const notificationId = FileHelper.getFileNotificationId(fileName);

      addCenterNotification({
        id: notificationId,
        title: translate('custom.notifications.notificationCenter.uploadDocument.uploadDocumentStarted'),
        message: translate('custom.notifications.notificationCenter.uploadDocument.uploadDocumentInProgress', {
          fileName
        }),
        timestamp: new Date(),
        type: 'progress',
        progress: {
          current: 0,
          total: fileSize,
          percent: 0,
          fileName
        }
      });
    },
    onError: (data, variables) => {
      showToast({
        type: GEAR_NOTIFICATION_TYPE.ERROR,
        message: translate('custom.documents.upload.startSessionFailed')
      });

      const fileName = variables.dto.chunk.file.name;
      const notificationId = FileHelper.getFileNotificationId(fileName);

      updateCenterNotification(notificationId, {
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.uploadDocument.uploadDocumentFailedTitle'),
        message: translate('custom.notifications.notificationCenter.uploadDocument.uploadDocumentFailedMessage', {
          fileName: variables.dto.chunk.file.name
        }),
        timestamp: new Date(),
        type: 'error'
      });
    }
  });
}
